import React, { Fragment, useEffect, useRef, type FC } from 'react';
// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions
import { useQuery } from 'react-apollo';
import { css } from '@compiled/react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import {
	ContentBlogUnifiedQuery,
	ContentUnifiedQueryArtifact,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useContentType } from '@confluence/page-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { HighlightActionsProviderForNav4 } from '@confluence/highlight-actions/entry-points/HighlightActionsProvider.ts';
import {
	useObjectSidebarActions,
	useIsObjectSidebarShown,
	useBehavior,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { ObjectSidebarPanel } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPanelWrapper.tsx';
import { ObjectSidebarPushPlaceholder } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPushPlaceholder.tsx';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	APPROVALS_PANE_CURRENT_CONTENT_STATUS,
	APPROVALS_PANE_ID,
	getApprovalsPaneContextKey,
	syncApprovalsPaneContextOwnership,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

import { ContentScreenComponent } from './ContentScreenComponent.tsx';
import { ContentContainerComponent } from './ContentContainerComponent.js';

const EMPTY_CONTENT_SCREEN_STYLES = {};
const EMPTY_LOOK_AND_FEEL = {};

const useContentRecommendationParam = createSingleQueryParamHook('contentRecommendation');

const contentWrapperStyles = css({
	display: 'block',
	flex: '2',
	width: '100%',
});

type ContentContainerProps = {
	contentId: string;
	fetchTheme?: boolean;
	children:
		| React.ReactNode
		| ((props: { isThemed: boolean; isLoading: boolean }) => React.ReactNode);
	isScreenContentView?: boolean;
	isContainerContentView?: boolean;
	spaceKey?: string;
	errorAttribution: string;
	contentScreenStyles?: any;
	stickyHeader?: React.ReactNode;
	versionOverride?: number | null;
	isEmbeddedPage?: boolean;
};

export const ContentContainer: FC<ContentContainerProps> = ({
	contentId,
	fetchTheme = false,
	children,
	isScreenContentView = false,
	isContainerContentView = false,
	spaceKey,
	errorAttribution = Attribution.BACKBONE as string,
	contentScreenStyles = EMPTY_CONTENT_SCREEN_STYLES,
	stickyHeader = null,
	versionOverride = null,
	isEmbeddedPage,
}) => {
	const isLivePage = useIsCurrentPageLive();
	const [contentType] = useContentType();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const behavior = useBehavior();
	const pageMode = useGetPageMode();

	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const contentRecommendationParam = useContentRecommendationParam();

	const rightSidebarContext = useRightSidebarContext();
	const isSidebarApprovalsPaneOpen =
		rightSidebarContext.getCurrent()?.panelId === APPROVALS_PANE_ID && pageMode === PageMode.VIEW;
	const approvalsPaneContextKey = getApprovalsPaneContextKey({
		contentId,
		contentStatus: APPROVALS_PANE_CURRENT_CONTENT_STATUS,
	});

	const rightSidebarContextRef = useRef(rightSidebarContext);
	rightSidebarContextRef.current = rightSidebarContext;

	useEffect(() => {
		void hideObjectSidebar();
	}, [contentId, hideObjectSidebar]);

	useEffect(() => {
		syncApprovalsPaneContextOwnership({
			isApprovalsExperimentEnabled: expValEquals('confluence_approvals_eap', 'isEnabled', true),
			isSidebarApprovalsPaneOpen,
			contextKey: approvalsPaneContextKey,
			closeApprovalsPane: () => rightSidebarContextRef.current.close(APPROVALS_PANE_ID),
		});
	}, [approvalsPaneContextKey, isSidebarApprovalsPaneOpen]);

	const isBlogpost = contentType === 'blogpost';
	const skipThemeQuery = !fetchTheme || !contentId;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	const pageThemeQueryResult = useQueryInterop<
		ContentUnifiedQueryType,
		ContentUnifiedQueryVariablesType
	>(ContentUnifiedQueryArtifact, {
		context: { single: true },
		skip: skipThemeQuery || isBlogpost,
		variables: {
			contentId,
			spaceKey,
			versionOverride,
			isSSR,
			useNewContentTopper: true,
		},
	});

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const blogThemeQueryResult = useQuery(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations
		ContentBlogUnifiedQuery,
		{
			context: { single: true },
			skip: skipThemeQuery || !isBlogpost,
			variables: {
				contentId,
				spaceKey,
				versionOverride,
				useNewContentTopper: true,
			},
		},
	);

	const queryResult = isBlogpost ? blogThemeQueryResult : pageThemeQueryResult;

	const shouldAddBannerPadding =
		!isLivePage &&
		contentRecommendationParam === 'on' &&
		fg('cc-engagement-nth-user-teammate-rollout-gate');

	if (queryResult.error) {
		return (
			<ErrorBoundary attribution={errorAttribution}>
				{children as React.ReactNode}
				<ErrorDisplay error={queryResult.error} />
			</ErrorBoundary>
		);
	} else {
		const theme = Boolean(queryResult?.data?.space?.theme);
		const lookAndFeel = queryResult?.data?.space?.lookAndFeel || EMPTY_LOOK_AND_FEEL;

		const childrenToRender =
			typeof children === 'function'
				? children({ isThemed: theme, isLoading: queryResult.loading })
				: children;

		return (
			<ErrorBoundary attribution={errorAttribution}>
				<ContentScreenComponent
					spaceKey={spaceKey}
					isThemed={theme}
					isLoading={queryResult.loading}
					lookAndFeel={lookAndFeel}
					isContentView={isScreenContentView}
					contentScreenStyles={contentScreenStyles}
					isLivePage={isLivePage}
					isEmbeddedPage={isEmbeddedPage}
				>
					{!theme && stickyHeader ? stickyHeader : null}
					<ContentContainerComponent
						isThemed={theme}
						lookAndFeel={lookAndFeel}
						isContentView={isContainerContentView}
						isLoading={queryResult.loading}
						isLivePage={isLivePage}
						isObjectSidebarEnabled={
							fg('confluence_frontend_content_wrapper') ||
							expValEquals('confluence_approvals_eap', 'isEnabled', true)
						}
						additionalPaddingTop={shouldAddBannerPadding ? 12 : 0}
					>
						{fg('confluence_frontend_content_wrapper') ||
						isRovoButtonEnabled ||
						expValEquals('confluence_approvals_eap', 'isEnabled', true) ? (
							<Fragment>
								<div css={contentWrapperStyles}>{childrenToRender}</div>
								{/* When panel-system flag is enabled, Object Sidebar renders at Root level via navigation-system Panel */}
								{!fg('ads-panel-system-confluence-object-sidebar') &&
									(fg('confluence_frontend_content_wrapper') || isRovoButtonEnabled) &&
									isObjectSidebarShown &&
									pageMode === PageMode.VIEW && (
										<Fragment>
											{behavior === 'push' && <ObjectSidebarPushPlaceholder />}
											<ObjectSidebarPanel />
										</Fragment>
									)}
							</Fragment>
						) : (
							childrenToRender
						)}
					</ContentContainerComponent>
					<HighlightActionsProviderForNav4 />
				</ContentScreenComponent>
			</ErrorBoundary>
		);
	}
};
